import { Injectable } from '@angular/core';

export type MacroTypes = 'supply' | 'demand' | 'demandCallbacks';

const CommonMacros = [
  {
    macro: '[SUPPLY_ID]',
    description: 'The ID of the Supply',
  },
  {
    macro: '[URL]',
    description: 'Full URL where the Supply is located',
  },
  {
    macro: '[DOMAIN]',
    description: 'Domain where the Supply is located',
  },
  {
    macro: '[WIDTH]',
    description: 'Width of the Supply',
  },
  {
    macro: '[HEIGHT]',
    description: 'Height of the Supply',
  },
  {
    macro: '[CONTAINER_WIDTH]',
    description: 'Width of the Supply',
  },
  {
    macro: '[CONTAINER_HEIGHT]',
    description: 'Height of the Supply',
  },
  {
    macro: '[CACHE_BUSTER]',
    description: 'Cache buster',
  },
  {
    macro: '[CB]',
    description: 'Cache buster',
  },
  {
    macro: '[IP]',
    description: 'User IP address',
  },
  {
    macro: '[UA]',
    description: 'User agent',
  },
  {
    macro: '[VIDEO_ID]',
    description: 'Content video/image ID',
  },
  {
    macro: '[VIDEO_DESCRIPTION]',
    description: 'Content video/image description',
  },
  {
    macro: '[VIDEO_DURATION]',
    description: 'Content video/image duration',
  },
  {
    macro: '[VIDEO_KEYWORDS]',
    description: 'Content video/image keywords',
  },
  {
    macro: '[VIDEO_FILE_URL]',
    description: 'Content video/image file URL',
  },
  {
    macro: '[VIDEO_TITLE]',
    description: 'Content video/image title',
  },
  {
    macro: '[VIDEO_CATEGORY]',
    description: 'Content video/image category according to IAB',
  },
  {
    macro: '[VIDEO_LENGTH]',
    description: 'Content video/image length',
  },
  {
    macro: '[DNT]',
    description: 'Do not track settings: 0 for no, 1 for yes',
  },

  {
    macro: '[CONN_TYPE]',
    description: 'Connection type',
  },
  {
    macro: '[DEVICE_CARRIER]',
    description: 'Device carrier',
  },
  {
    macro: '[DEVICEOS]',
    description: 'Device OS',
  },
  {
    macro: '[DEVICEOSV]',
    description: 'Device OS version',
  },
  {
    macro: '[DEVICEMAKE]',
    description: 'Device make',
  },
  {
    macro: '[DEVICEMODEL]',
    description: 'Device model',
  },
  {
    macro: '[DEVICEID]',
    description: 'Device ID',
  },
  {
    macro: '[APP_NAME]',
    description: 'App name',
  },
  {
    macro: '[APP_VER]',
    description: 'App version',
  },
  {
    macro: '[APP_BUNDLE]',
    description: 'App bundle',
  },
  {
    macro: '[APP_STORE_URL]',
    description: 'App store URL',
  },
  {
    macro: '[APP_IDFA]',
    description: 'iOS advertising ID',
  },
  {
    macro: '[APP_AID]',
    description: 'Android advertising ID',
  },
  {
    macro: '[APP_IDFA_MD5]',
    description: 'iOS advertising ID, replaced by Supply parameter "appidfa" and MD5 hashed',
  },
  {
    macro: '[APP_AID_MD5]',
    description: 'Android advertising ID, replaced by Supply parameter "appaid" and MD5 hashed',
  },
  {
    macro: '[APP_IDFA_SHA1]',
    description: 'iOS advertising ID, replaced by Supply parameter "appidfa" and SHA1 hashed',
  },
  {
    macro: '[APP_AID_SHA1]',
    description: 'Android advertising ID, replaced by Supply parameter "appaid" and SHA1 hashed',
  },
  {
    macro: '[APP_STORE_ID]',
    description: 'App store ID',
  },
  {
    macro: '[APP_CATEGORY]',
    description: 'App category',
  },
  {
    macro: '[COUNTRY_ID]',
    description: 'Country',
  },
  {
    macro: '[LOCATION]',
    description: 'Location',
  },
  {
    macro: '[LOCATION_LONG]',
    description: 'Location longitude',
  },
  {
    macro: '[LOCATION_LAT]',
    description: 'Location latitude',
  },
  {
    macro: '[AD_POSITION]',
    description: 'Position of the Ad on users screen replaced by Supply parameter "adposition"',
  },
  {
    macro: '[PAGE_CATEGORY]',
    description: 'Page category according to IAB replaced by Supply parameter "pagecat"',
  },
  {
    macro: '[SITE_TITLE]',
    description: 'The title of the site replaced by Supply parameter "sitetitle"',
  },
  {
    macro: '[SITE_CATEGORY]',
    description: 'Site category according to IAB replaced by Supply parameter "sitecat"',
  },
  {
    macro: '[SUBID]',
    description: 'Value replaced by Supply parameter "sid"',
  },
  {
    macro: '[REFERRER_URL]',
    description: 'Referrer URL (URL of the webpage that redirected the user to the page of the Supply)',
  },
  {
    macro: '[CUSTOM1]-[CUSTOM20]',
    description: 'Custom macro replaced by Supply parameter "c1" to "c20"',
  },
  {
    macro: '[IFA]',
    description: 'Advertising ID',
  },
  {
    macro: '[IFA_MD5]',
    description: 'MD5 hashed Advertising ID',
  },
  {
    macro: '[IFA_HEX]',
    description: 'HEX hashed Advertising ID',
  },
  {
    macro: '[IFA_SHA1]',
    description: 'SHA1 hashed Advertising ID',
  },
  {
    macro: '[GDPR]',
    description: 'Indicates whether the GDPR consent by the user is needed, if yes will pass 1, otherwise will pass 0',
  },
  {
    macro: '[GDPR_CONSENT]',
    description: 'Passes the IAB user consent  for sharing data string',
  },
  {
    macro: '[US_PRIVACY]',
    description: 'US Privacy/CCPA string, e.g. "1YNN"',
  },
  {
    macro: '[SCHAIN]',
    description:
      'Complete SChain string as received by supply, e.g. "1.0,1!exchange1.com,1234,1,bid-request-1,publisher,publisher.com"',
  },
  {
    macro: '[SCHAIN_SID]',
    description:
      'First sid from the schain object received by supply, e.g. 1234 from "1.0,1!exchange1.com,1234,1,bid-request-1,publisher,publisher.com"',
  },
];

const SpecificMacros: { [k: string]: MacroList } = {
  supply: [
    {
      macro: '[DEMAND_ID]',
      description: 'The ID of the Demand',
    },
    {
      macro: '[AUTOPLAY]',
      description: 'Ad was autoplayed: 0 for no, 1 for yes',
    },
    {
      macro: '[MUTED]',
      description: 'Ad was played muted: 0 for no, 1 for yes',
    },
  ],
  demand: [
    {
      macro: '[DEMAND_ID]',
      description: 'The ID of the Demand',
    },
    {
      macro: '[AUTOPLAY]',
      description: 'Ad was autoplayed: 0 for no, 1 for yes',
    },
    {
      macro: '[MUTED]',
      description: 'Ad was played muted: 0 for no, 1 for yes',
    },
  ],
  demandCallbacks: [
    {
      macro: '[DEMAND_ID]',
      description: 'The ID of the Demand',
    },
    {
      macro: '[AUTOPLAY]',
      description: 'Ad was autoplayed: 0 for no, 1 for yes',
    },
    {
      macro: '[MUTED]',
      description: 'Ad was played muted: 0 for no, 1 for yes',
    },
    {
      macro: '[RPM]',
      description:
        'Demand Revenue ($) per 1000 Ad Impressions (encrypted), available for use in Ad Start, Ad Complete and Ad Click events only',
    },
  ],
};

export type MacroList = typeof CommonMacros;

@Injectable()
export class MacrosService {
  constructor() {}

  getMacros(type: MacroTypes) {
    return [...CommonMacros, ...SpecificMacros[type]].sort((a, b) => a.macro.localeCompare(b.macro));
  }

  getMacrosName(type: MacroTypes = 'demand') {
    return [...CommonMacros, ...SpecificMacros[type]].map((a) => a.macro);
  }
}
